import {
  getCoachNavItemsWithAcademy,
  getGuardianNavItemsWithAcademy,
  getNavItemsByRole,
  type SidebarNavItem,
} from '@/components/layout/sidebar-nav-config';

export type DashboardRole =
  | 'academy_owner'
  | 'academy_admin'
  | 'club_owner'
  | 'club_admin'
  | 'guardian'
  | 'admin'
  | 'coach';

export type DashboardBreadcrumb = { label: string; href?: string };

export function getDashboardHomePath(
  dashboardRole: DashboardRole,
  academySlug?: string,
  orgDashboardPath?: string
): string {
  if (orgDashboardPath) {
    return orgDashboardPath;
  }
  if (dashboardRole === 'guardian' && academySlug) {
    return `/academy/${academySlug}/dashboard`;
  }
  if (dashboardRole === 'coach' && academySlug) {
    return `/academy/${academySlug}/coach`;
  }
  if (isOrganizationManagementRole(dashboardRole)) {
    return '/admin/dashboard';
  }
  return '/dashboard';
}

function isOrganizationManagementRole(
  role: DashboardRole
): role is 'academy_owner' | 'academy_admin' | 'club_owner' | 'club_admin' {
  return (
    role === 'academy_owner' ||
    role === 'academy_admin' ||
    role === 'club_owner' ||
    role === 'club_admin'
  );
}

function getNavItemsForRole(
  dashboardRole: DashboardRole,
  academySlug?: string,
  unifiedNavItems?: SidebarNavItem[]
): SidebarNavItem[] {
  if (dashboardRole === 'guardian' && academySlug) {
    return getGuardianNavItemsWithAcademy(academySlug);
  }
  if (dashboardRole === 'coach' && academySlug) {
    return getCoachNavItemsWithAcademy(academySlug);
  }
  if (unifiedNavItems?.length) {
    return unifiedNavItems;
  }
  return getNavItemsByRole(dashboardRole);
}

function findLongestNavMatch(pathname: string, navItems: SidebarNavItem[]): SidebarNavItem | null {
  const sorted = [...navItems].sort((a, b) => b.to.length - a.to.length);
  return (
    sorted.find(
      (item) => pathname === item.to || (item.to !== '/' && pathname.startsWith(`${item.to}/`))
    ) ?? null
  );
}

export function resolveDashboardBreadcrumbs(opts: {
  dashboardRole: DashboardRole;
  pathname: string;
  academySlug?: string;
  title?: string;
  custom?: DashboardBreadcrumb[];
  homePath?: string;
  homeLabel?: string;
  unifiedNavItems?: SidebarNavItem[];
}): DashboardBreadcrumb[] {
  const {
    dashboardRole,
    pathname,
    academySlug,
    title,
    custom,
    homePath: homePathOverride,
    homeLabel,
    unifiedNavItems,
  } = opts;
  const homePath = homePathOverride ?? getDashboardHomePath(dashboardRole, academySlug);
  const homeCrumb: DashboardBreadcrumb = {
    label: homeLabel ?? 'Home',
    href: homePath,
  };

  if (pathname === homePath || pathname === '/dashboard') {
    return [homeCrumb];
  }

  if (custom && custom.length > 0) {
    if (custom[0]?.label === 'Home') {
      return custom;
    }
    const trail = [homeCrumb, ...custom];
    const lastLabel = custom[custom.length - 1]?.label?.trim().toLowerCase();
    const titleNorm = title?.trim().toLowerCase();
    if (titleNorm && lastLabel !== titleNorm) {
      trail.push({ label: title! });
    }
    return trail;
  }

  const navItems = unifiedNavItems?.length
    ? unifiedNavItems
    : getNavItemsForRole(dashboardRole, academySlug);
  const navMatch = findLongestNavMatch(pathname, navItems);

  if (!navMatch) {
    if (title && title.trim()) {
      const onHome = pathname === homePath || pathname === '/dashboard';
      return onHome ? [homeCrumb] : [homeCrumb, { label: title }];
    }
    return [homeCrumb];
  }

  const onNavRoot = pathname === navMatch.to;
  const titleDiffersFromNav =
    title && title.trim() && title.trim().toLowerCase() !== navMatch.label.toLowerCase();

  if (onNavRoot && !titleDiffersFromNav) {
    return [homeCrumb, { label: navMatch.label }];
  }

  if (onNavRoot && titleDiffersFromNav) {
    return [homeCrumb, { label: navMatch.label, href: navMatch.to }, { label: title! }];
  }

  if (titleDiffersFromNav) {
    return [
      homeCrumb,
      { label: navMatch.label, href: navMatch.to },
      { label: title! },
    ];
  }

  return [homeCrumb, { label: navMatch.label, href: navMatch.to }];
}
